import {
  Component, OnInit, OnDestroy, ChangeDetectorRef, Input, ViewChild, Output, EventEmitter
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';

import { Subject, Observable } from 'rxjs';
import { takeUntil, skip, take, distinctUntilChanged, debounceTime, map } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AppState, Selectors, Actions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';

import { SetUserNotificationsPayload, UserPayload, CustomerError, GetCurrentDomainDefaultNotifiedUsersPayload } from 'store/models/customer.models';
import { Domain } from 'modules/shared/models/domain.models';
import { FilterItem, Filter } from 'modules/shared/models/filter.models';

import { UserNotificationDataSource, UserNotificationRow } from 'modules/shared/data-source/user-notifications.data-source';

import { SnackbarService } from 'app/services/snackbar.service';

@Component({
  selector: 'artemis-user-notifications-component',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss'],
})

export class UserNotificationsComponent implements OnInit, OnDestroy {

  @Input() showActionButtons: boolean = true;
  @Input() showResetButton: boolean = true;
  @Input() showPopover: boolean = true;
  @Input() takeDefaultUsers: boolean = true;

  @Input() set newDomain(domain: Domain) {
    if (domain) {
      this.selectedDomain = Object.assign({}, domain);
      this.dataSource.setCurrentDomain(domain);
      this.payload.domainObject = this.selectedDomain;
      this.dataSource.loadUserData(this.payload);
    }
  }

  @Input() set allDomains(domains: Array<Domain>) {
    this._allDomains = domains.slice();
  }

  @Output() submitUpdatedNotificationList: EventEmitter<Array<UserNotificationRow>> = new EventEmitter();
  @Output() defaultUsersSaved: EventEmitter<boolean> = new EventEmitter();

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  private payload: UserPayload = {};
  private unsubscribe$: Subject<void> = new Subject();

  readonly pageSizeOptions: Array<number> = [5, 10, 15];
  filter: Filter;

  _allDomains: Array<Domain> = [];
  selectedDomain: Domain;

  loading$: Observable<boolean>;

  displayedColumns: Array<string> = ['checked', 'user', 'l1', 'l2', 'l3'];
  dataSource: UserNotificationDataSource;
  searchFormControl: FormControl;

  constructor(
    private snackbar: SnackbarService,
    private store: Store<AppState>,
    private cdRef: ChangeDetectorRef,
  ) {
    this.filter = {
      title: 'ROLES',
      items: ['L1', 'L2', 'L3'].map((roleValue: string) => ({ subType : 'ROLE', key: roleValue }))
    };
  }

  ngOnInit(): void {
    this.getLoadingStatus();

    this.searchFormControl = new FormControl('');

    this.getCurrentDomain();
    this.initSearchSubscription();
    this.checkErrors();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading$ = this.store.select(Selectors.customer.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((customerLoading: Array<CustomerActionTypes>) => {
          return customerLoading.includes(CustomerActionTypes.GET_USERS);
        })
      );
  }

  private getCurrentDomain(): void {
    this.store
      .select(Selectors.domain.getCurrentDomain)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (domain: Domain) => {
          this.selectedDomain = domain;
          this.payload.domainObject = this.selectedDomain;
          this.setDataSource();
          this.dataSource.loadUserData(this.payload);
        }
      );
  }

  private initSearchSubscription(): void {
    this.searchFormControl
      .valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
      )
      .subscribe(
        (value: string) => {
          this.paginator.pageIndex = 0;
          this.payload.searchName = value;
          this.dataSource.loadUserData(this.payload);
        }
      );
  }

  /**
   * Prepares data source
   * @method setDataSource
   */
  private setDataSource(): void  {
    this.dataSource = new UserNotificationDataSource(this.store, this.takeDefaultUsers, this.selectedDomain);
    this.dataSource.paginator = this.paginator;
    this.dataSource.paginator.pageSizeOptions = this.pageSizeOptions;
  }

  /**
   * Check errors in the store for relevant
   * @method checkErrors
   */
  private checkErrors(): void {
    this.store
      .select(Selectors.customer.getErrors)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (errors: Array<CustomerError>) => {
          if (errors.some((error: CustomerError) => error.action === CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS)) {
            this.snackbar.error('Failed to set default notified users, please try again later.');
            this.dataSource.loadUserData();
          }

          this.cdRef.detectChanges();
        }
      );
  }

  onSelectionChangeHandler(data: MatSelectChange): void {
    this.payload.roles = data.value.map((selectedItem: FilterItem) => selectedItem.key).join(',');

    this.paginator.pageIndex = 0;
    this.dataSource.loadUserData(this.payload);
  }

  onDomainFilterHandler($event: MatSelectChange): void {
    const selectedDomainId: number = this._allDomains.findIndex((domain: Domain) => domain.id === $event.value);

    if (selectedDomainId >= 0) {
      this.selectedDomain = this._allDomains[selectedDomainId];
      this.payload.domainObject = this.selectedDomain;

      this.store.dispatch(new Actions.customer.GetDefaultNotifiedUsers(<GetCurrentDomainDefaultNotifiedUsersPayload>{
        domain: this.selectedDomain
      }));
    }

    this.dataSource.paginator.pageIndex = 0;

    this.dataSource.loadUserData(this.payload);
    this.cdRef.detectChanges();
  }

  /**
   * Clear default users
   * @method clearDefaultUsers
   */
  clearDefaultUsers(): void {
    this.dataSource.clearAllSelected();
    const domainSelectedUsersPayload: SetUserNotificationsPayload = {
      domainId: this.selectedDomain.id,
      users: []
    };

    this.store.select(Selectors.customer.getDefaultNotifiedUsers)
      .pipe(
        skip(1),
        take(1)
      )
      .subscribe(() => {
        this.snackbar.success('Preferences successfully removed.' +
                              ' You will need to select users to notify each time you onboard a customer.');
      });

    this.store.dispatch(new Actions.customer.SetDefaultNotifiedUsers(domainSelectedUsersPayload));
    this.cdRef.detectChanges();
  }

  updateAllNotifications(user: UserNotificationRow, event: MatCheckboxChange): void {
    this.dataSource.selectItem(user, event.checked);
    this.cdRef.detectChanges();
  }

  saveSelectedUsers(): void {
    const domainSelectedUsersPayload: SetUserNotificationsPayload = {
      domainId: this.selectedDomain.id,
      users: this.dataSource.getSelectedUsers()
    };

    const isSelectedUserValid: boolean =
      this.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l1) &&
      this.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l2) &&
      this.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l3);

    if (!isSelectedUserValid) {
      this.snackbar.error('Select at least one L1 user, one L2 user and one L3 user before saving.');
    } else {
      this.store.dispatch(new Actions.customer.SetDefaultNotifiedUsers(domainSelectedUsersPayload));
      this.defaultUsersSaved.emit(true);
      this.snackbar.success('Preferences successfully updated.');
    }
  }

  /**
   * Submit selected users to update access control for a certain customer
   * @method submitSelected
   */
  submitSelected(): void {
    const isSelectedUserValid: boolean =
      this.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l1) &&
      this.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l2) &&
      this.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l3);

    if (isSelectedUserValid) {
      this.submitUpdatedNotificationList.emit(this.dataSource.getSelectedUsers());
    } else {
      this.snackbar.error('Select at least one L1 user, one L2 user and one L3 user before saving.');
    }
  }
}
